import React from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../../../generic/hooks/useDateFormatter'
import { workerFullName } from '../../../utils/jobDetailFormat'
import { absenceMode } from '../../../utils/jobDetailFootprint'
import {
  INSPECTION_RESULT,
  footprintSpecificationText,
  groupFootprintInspections,
  resultKind,
  signedQuantity,
  typeLabelKey
} from '../../../utils/jobDetailInspections'
import { DeviationBar } from '../tabs/InspectionsTab'
import { HeaderCell, Pill } from '../JobDetailPrimitives'

// Historia 169 (RN18, C4, CU3), enmienda UX-9 (03-dominio.md, tras la revisión del PO en mic1) — D6b
// se pinta con la MISMA presentación que la pestaña Inspecciones del job (#160 S73/S74,
// `tabs/InspectionsTab.jsx`): una sección por grupo con chip de cobertura DE ESTA PIEZA
// (`groupFootprintInspections`, UX-9) y una tabla Código · Inspección · Tipo · Especificación ·
// Resultado · Herramienta (SIN "Piezas reportadas": esto es una sola pieza, no un agregado). El
// resultado es el de ESTA pieza: BOOLEAN → chip OK/NOK (nunca "Valor medido: true" ni "En
// tolerancia"), RANGE → valor + desviación + la misma barra (`DeviationBar`, reutilizada de la
// pestaña), LIST/TEXT/DATE → el valor, sin reporte → "Not measured"/"Not measured due to sampling",
// NO_VERDICT (sin ninguno de los anteriores) → "No verdict" literal.
//
// UX-4/UX-G11: la línea pequeña de "medido por / fecha / responsable / instrumento" solo con los
// presentes; un ausente → icono `Absent` (HelpOutline + tooltip "Sin registro", NUNCA el carácter
// "—" — UX-9 lo pide explícito, a diferencia de MATERIALES donde SÍ se conserva "—" para
// componentes pieza); los 3 ausentes a la vez → "Report without author or instrument" (ya existía).
//
// H21/B-J11 (2026-09-27, FIRMADO — S79, hub e049d60): la columna Especificación usa
// `footprintSpecificationText`, NUNCA `specificationText` de la pestaña — C4 no publica `options`
// (R-HANDOFF-1), así que una LIST siempre queda vacía en la huella (nunca "A · B · C" ni el "—" que la
// pestaña pinta con una LIST vacía); una RANGE sin `nominal` también queda vacía. El distintivo de
// muestreo "1 de cada N" (clave existente `1 of every {{count}}`, `InspectionsTab.jsx`) se pinta junto
// al nombre con `frequency` > 1, igual que en la pestaña.

const GRID = 'minmax(0, 0.9fr) minmax(0, 2fr) minmax(0, 0.8fr) minmax(0, 1.3fr) minmax(0, 1.5fr) minmax(0, 1fr)'

const notMeasuredKey = (line) => ((line.frequency || 0) > 1 ? 'Not measured due to sampling' : 'Not measured')
const notMeasuredTone = (line) => ((line.frequency || 0) > 1 ? 'default' : 'warning')
const isPresentValue = (value) => value !== null && value !== undefined

const Absent = ({ testId }) => {
  const { t_ } = useSmartTranslation()
  return (
    <Tooltip title={t_('Not recorded')} disableInteractive>
      <HelpOutlineIcon fontSize='inherit' data-testid={testId} sx={{ color: 'text.secondary', verticalAlign: 'middle' }} />
    </Tooltip>
  )
}
Absent.propTypes = { testId: PropTypes.string }

/**
 * UX-9 (precisión (2)/(4) del orquestador, tras leer el literal definitivo de S79): el resultado de
 * ESTA pieza. `status` manda SOLO para "sin medir" (NOT_MEASURED, con o sin muestreo); el resto lo
 * decide el TIPO (`resultKind`, la MISMA clasificación de la pestaña, RN65): BOOLEAN → chip OK/NOK,
 * RANGE → valor + desviación (tono de ERROR si está fuera de tolerancia) + la barra, LIST (NO_VERDICT
 * en `resultKind`) → el valor CON la marca "Sin veredicto" al lado, TEXT/DATE (REPORTED) → solo el
 * valor, NO_RESPONSE (NONE) → celda VACÍA (ni "—" ni icono, precisión (4): "el resultado de una
 * informativa se deja vacío").
 */
const ResultCell = ({ line, language }) => {
  const { t_ } = useSmartTranslation()
  const code = line.code
  const testId = 'footprint-inspection-status-' + code
  if (line.status === 'NOT_MEASURED') {
    return <Pill label={t_(notMeasuredKey(line))} tone={notMeasuredTone(line)} testId={testId} />
  }
  const kind = resultKind(line)
  if (kind === INSPECTION_RESULT.BOOLEAN) {
    const ok = line.status === 'IN_TOLERANCE'
    return <Pill label={(ok ? t_('OK') : t_('NOK')).trim()} tone={ok ? 'success' : 'error'} testId={testId} />
  }
  if (kind === INSPECTION_RESULT.RANGE) {
    const outOfTolerance = line.status === 'OUT_OF_TOLERANCE'
    const showBar = isPresentValue(line.value) && isPresentValue(line.nominal) && isPresentValue(line.lowerTolerance) && isPresentValue(line.upperTolerance)
    return (
      <Stack spacing={0.5} data-testid={testId}>
        <Typography sx={{ fontWeight: 700, color: outOfTolerance ? 'error.light' : 'inherit' }}>
          {line.value}{isPresentValue(line.deviation) ? ' (' + signedQuantity(line.deviation, language) + ')' : ''}
        </Typography>
        {showBar ? <DeviationBar line={{ ...line, lastValue: line.value, lastDeviation: line.deviation }} language={language} /> : null}
      </Stack>
    )
  }
  if (kind === INSPECTION_RESULT.NO_VERDICT) {
    return (
      <Stack direction='row' spacing={1} alignItems='center' data-testid={testId}>
        <Typography>{isPresentValue(line.value) ? line.value : ''}</Typography>
        <Pill label={t_('No verdict')} testId={testId + '-verdict'} />
      </Stack>
    )
  }
  if (kind === INSPECTION_RESULT.REPORTED) {
    return <Typography data-testid={testId}>{isPresentValue(line.value) ? line.value : ''}</Typography>
  }
  // INSPECTION_RESULT.NONE (NO_RESPONSE): celda vacía, ni "—" ni icono.
  return <Box data-testid={testId} />
}

ResultCell.propTypes = { line: PropTypes.object, language: PropTypes.string }

const InspectionRow = ({ line, language }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const code = line.code
  const toolNames = (line.tools || []).map((tool) => tool.name).filter((name) => name)
  const hasWorker = !!(line.report && line.report.worker)
  const hasResponsible = !!(line.report && line.report.responsible)
  const hasInstrument = !!(line.report && line.report.instrumentSerialNumber)
  const mode = line.report ? absenceMode([hasWorker, hasResponsible, hasInstrument]) : 'ALL_PRESENT'
  return (
    <Box data-testid={'footprint-inspection-' + code} sx={{ borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Box role='row' sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2, py: 1, minHeight: 54 }}>
        {/* UX-9 (FIRMADO — S79): `overflowWrap: 'anywhere'` partía un código largo (p.ej.
            "0911571000_0900_1_20") en dos líneas dentro de su columna estrecha — nowrap + elipsis con
            tooltip (mismo patrón ya usado en `name`/`toolNames` de esta fila) en vez de partirlo. */}
        <Typography data-testid={'footprint-inspection-code-' + code} noWrap title={code} sx={{ fontFamily: 'monospace' }}>{code}</Typography>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
          <Typography noWrap title={line.name} sx={{ minWidth: 0 }}>{line.name}</Typography>
          {/* H21/B-J11: el mismo distintivo de la pestaña (`InspectionsTab.jsx`) para la característica
              de muestreo — "1 de cada N", solo con frecuencia > 1. */}
          {(line.frequency || 0) > 1
            ? <Pill label={t_('1 of every {{count}}', { count: line.frequency })} tone='info' testId={'footprint-inspection-frequency-' + code} />
            : null}
        </Stack>
        <Tooltip title={t_(typeLabelKey(line.dataType))} disableInteractive>
          <span><Pill label={t_(typeLabelKey(line.dataType))} testId={'footprint-inspection-type-' + code} /></span>
        </Tooltip>
        <Typography sx={{ overflowWrap: 'anywhere' }} color='text.secondary'>
          {/* H21/B-J11 (2026-09-27, FIRMADO — S79): la especificación en la huella es más pobre que la
              de la pestaña — TEXT/DATE, LIST y RANGE sin nominal quedan VACÍOS, nunca "—": C4 no publica
              `options`, así que esta fila NUNCA los lee (R-HANDOFF-1). `footprintSpecificationText`
              (utils/jobDetailInspections.js) es la única fuente de esta celda; `specificationText`, que
              la pestaña sigue usando sin cambios, no se toca. */}
          {footprintSpecificationText(line, t_, language)}
        </Typography>
        <ResultCell line={line} language={language} />
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap title={toolNames.join(', ')}>{toolNames.join(', ')}</Typography>
        </Box>
      </Box>
      {/* UX-9/S95 (literal definitivo): "Medido por {{worker}} · {{fecha}} · Responsable {{resp}} ·
          Instrumento {{instr}}" — sin rótulo delante de la fecha, orden fijo Worker/Fecha/
          Responsible/Instrument (o el icono Absent si falta solo uno), sin colon tras el rótulo. TODO
          en un único `Typography` NO-flex (B-J-space, mic1): los " · " son texto interior normal,
          nunca el borde de un flex-item que un navegador real recortaría. */}
      {line.report
        ? (
          <Typography variant='body2' color='text.secondary' sx={{ px: 2, pb: 1 }}>
            {hasWorker
              ? (
                <>
                  <Box component='span' data-testid={'footprint-inspection-worker-' + code}>{t_('Measured by')} {workerFullName(line.report.worker)}</Box>
                  {' · '}
                </>
                )
              : null}
            <Box component='span'>{formatDateTime(line.report.reportDate)}</Box>
            {mode === 'MERGE'
              ? <>{' · '}{t_('Report without author or instrument')}</>
              : (
                <>
                  {hasResponsible ? <>{' · '}<Box component='span' data-testid={'footprint-inspection-responsible-' + code}>{t_('Responsible')} {line.report.responsible}</Box></> : null}
                  {hasInstrument ? <>{' · '}<Box component='span' data-testid={'footprint-inspection-instrument-' + code}>{t_('Instrument')} {line.report.instrumentSerialNumber}</Box></> : null}
                  {mode === 'ONE_MISSING' ? <>{' '}<Absent testId={'footprint-inspection-absent-' + code} /></> : null}
                </>
                )}
          </Typography>
          )
        : null}
    </Box>
  )
}

InspectionRow.propTypes = { line: PropTypes.object, language: PropTypes.string }

const InspectionSection = ({ section, language }) => {
  const { t_ } = useSmartTranslation()
  const title = section.noGroup ? t_('No group') : section.name
  return (
    <Box data-testid='footprint-inspection-section' component='section' aria-label={title} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1, bgcolor: 'background.row2' }}>
        <Typography sx={{ fontWeight: 800 }} data-testid={'footprint-inspection-section-title-' + section.key}>{title}</Typography>
        {/* UX-9, precisión (3): un grupo sin ninguna reportable (solo informativas) no lleva chip. */}
        {section.coverageKey
          ? (
            <Pill
              label={t_(section.coverageKey, section.coverageParams)}
              tone={section.coverageKey === 'Complete' ? 'success' : 'default'}
              testId={'footprint-inspection-section-chip-' + section.key}
            />
            )
          : null}
      </Stack>
      <Box role='table' aria-label={title}>
        <Box role='row' sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <HeaderCell>{t_('Code')}</HeaderCell>
          <HeaderCell>{t_('Inspection')}</HeaderCell>
          <HeaderCell>{t_('Type')}</HeaderCell>
          <HeaderCell>{t_('Specification')}</HeaderCell>
          <HeaderCell>{t_('Result')}</HeaderCell>
          <HeaderCell>{t_('Inspection Tool')}</HeaderCell>
        </Box>
        {section.lines.map((line) => <InspectionRow key={line.featureId} line={line} language={language} />)}
      </Box>
    </Box>
  )
}

InspectionSection.propTypes = { section: PropTypes.object, language: PropTypes.string }

const FootprintInspectionDetail = ({ data = {} }) => {
  const { i18n } = useSmartTranslation()
  const language = i18n?.language || 'en'
  const lines = data.lines || []
  const sections = groupFootprintInspections(lines)
  return (
    <Box>
      {sections.map((section) => <InspectionSection key={section.key} section={section} language={language} />)}
    </Box>
  )
}

FootprintInspectionDetail.propTypes = { data: PropTypes.object }

export default React.memo(FootprintInspectionDetail)
